import { css } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { CanvasComponent } from "./CanvasComponent";

/** CSS affecting the {@link SeaMonsterBattleComponent} */
const styles: string = css`
    :host {
        display: flex;
        align-items: center;
        justify-content: center;
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background-color: rgba(0, 0, 0, 0.8);
        z-index: 100;
        overflow: hidden;
    }
    .container {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 90%;
        height: 90%;
        max-width: 1200px;
        max-height: 800px;
        background-image: url("/assets/img/rooms/sea_beast_room.png");
        background-repeat: no-repeat;
        background-size: cover;
        border-radius: 10px;
        box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
    }
    .battle-ui {
        position: absolute;
        bottom: 40px;
        width: 100%;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 20px;
    }
    .prompt-container {
        position: relative;
        width: 200px;
        height: 200px;
        display: flex;
        align-items: center;
        justify-content: center;
    }
    .key-prompt {
        width: 100px;
        height: 100px;
        border-radius: 50%;
        background-color: rgba(0, 0, 0, 0.7);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 3rem;
        font-weight: bold;
        border: 4px solid white;
        user-select: none;
    }
    .key-prompt.correct {
        background-color: #4CAF50;
        transform: scale(1.1);
        transition: all 0.2s ease;
    }
    .key-prompt.incorrect {
        background-color: #f44336;
        transform: scale(0.9);
        transition: all 0.2s ease;
    }
    .timer-ring {
        position: absolute;
        width: 200px;
        height: 200px;
        border-radius: 50%;
        border: 8px solid transparent;
        border-top-color: #f44336;
        transition: transform 0.05s linear;
        transform: rotate(0deg);
    }
    .monster-health {
        position: absolute;
        top: 30px;
        right: 30px;
        width: 300px;
        height: 25px;
        background-color: #333;
        border-radius: 5px;
        overflow: hidden;
    }
    .monster-health-bar {
        height: 100%;
        width: 100%;
        background-color: #f44336;
        transition: width 0.3s ease;
    }
    .monster-image {
        width: 80%;
        height: 60%;
        object-fit: contain;
        position: absolute;
    }
    .cutscene-container {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: 1000000; /* Higher than the battle component */
        pointer-events: none; /* Allow clicks to pass through when not needed */
    }
    .countdown {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        font-size: 10rem;
        color: white;
        text-shadow: 0 0 10px red;
        z-index: 1000;
        opacity: 1;
        transition: opacity 0.5s ease-out;
    }

    /* Optional animation classes */
    @keyframes pulse {
        0% { transform: scale(1); }
        50% { transform: scale(1.2); }
        100% { transform: scale(1); }
    }
    .countdown-pulse {
        animation: pulse 0.5s ease-in-out;
    }
`;

export class SeaBeastBattleComponent extends HTMLElement {
    private promptElement!: HTMLDivElement;
    private timerRing!: HTMLDivElement;
    private monsterHealthBar!: HTMLDivElement;
    private timerInterval?: number;
    private timeLeft: number = 0;
    private totalTime: number = 2000; // 2 seconds to react
    private monsterHealth: number = 100;
    private currentKey: string = "E";
    private boundHandleKeyPress!: (event: KeyboardEvent) => void;
    private countDown!: HTMLDivElement;
    private countdownInterval?: number;
    private cutsceneNumber!: number;
    private readonly _gameRouteService: GameRouteService = new GameRouteService();
    private readonly _gameEventService: GameEventService = new GameEventService();

    /**
     * Called when the element is added to the DOM.
     * Initializes the shadow DOM and renders the component.
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this.soundController();
        this.render();
    }

    /**
     * Renders the component's HTML and sets up event listeners.
     * @private
     */
    private render(): void {
        if (!this.shadowRoot) return;

        this.shadowRoot.innerHTML = `
            <style>${styles}</style>
            <div class="container">
                <img class="monster-image" src="/assets/img/rooms/sea_beast.png" alt="Sea Beast">
                <div class="monster-health">
                    <div class="monster-health-bar" style="width: ${this.monsterHealth}%"></div>
                </div>
                <div class="battle-ui">
                    <div class="prompt-container">
                        <div class="timer-ring"></div>
                        <div class="key-prompt">${this.currentKey}</div>
                    </div>
                </div>
                <div class="countdown">
                </div>
            </div>
        `;

        this.promptElement = this.shadowRoot.querySelector(".key-prompt") as HTMLDivElement;
        this.timerRing = this.shadowRoot.querySelector(".timer-ring") as HTMLDivElement;
        this.monsterHealthBar = this.shadowRoot.querySelector(".monster-health-bar") as HTMLDivElement;
        this.countDown = this.shadowRoot.querySelector(".countdown") as HTMLDivElement;

        this.setupEventListeners();
        this.startFirstRound();
    }

    private soundController(): void {
        this._gameEventService.StopAudio();
        this._gameEventService.playSound("fight-music.mp3");
    }

    /**
     * Sets up event listeners for the battle minigame.
     * @private
     */
    private setupEventListeners(): void {
        document.removeEventListener("keydown", this.boundHandleKeyPress);
        this.boundHandleKeyPress = this.handleKeyPress.bind(this);
        document.addEventListener("keydown", this.boundHandleKeyPress);
    }

    private startFirstRound(): void {
        // Clear any existing intervals
        if (this.countdownInterval) clearInterval(this.countdownInterval);
        if (this.timerInterval) clearInterval(this.timerInterval);

        // Initialize the FIRST key
        this.currentKey = "E"; // Force start with E
        this.promptElement.textContent = this.currentKey;
        // console.log("Starting new round with key:", this.currentKey);

        let count: number = 3;
        this.countDown.textContent = count.toString();

        this.countdownInterval = window.setInterval(() => {
            count--;
            this.countDown.textContent = count.toString();

            if (count <= 0) {
                clearInterval(this.countdownInterval);
                this.countDown.style.opacity = "0";
                setTimeout(() => {
                    this.countDown.textContent = "";
                    this.startNewRound(); // This will set a random key
                }, 500);
            }
        }, 1000);
        // console.log("first round key:", this.currentKey);
    }

    /**
     * Starts a new attack round with a random key.
     * @private
     */
    private startNewRound(): void {
        // Choose a random key (A-Z)
        this.currentKey = String.fromCharCode(65 + Math.floor(Math.random() * 26));
        this.promptElement.textContent = this.currentKey;

        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }

        this.timeLeft = this.totalTime;
        this.updateTimer();

        this.timerInterval = window.setInterval(() => {
            this.timeLeft -= 50;
            this.updateTimer();

            if (this.timeLeft <= 0) {
                this.failedDodge();
            }
        }, 50);
    }

    /**
     * Updates the visual timer display.
     * @private
     */
    private updateTimer(): void {
        const progress: number = 360 * (this.timeLeft / this.totalTime);
        this.timerRing.style.transform = `rotate(${progress}deg)`;
    }

    /**
     * Handles keyboard press events.
     * @param event - The keyboard event
     * @private
     */
    private handleKeyPress(event: KeyboardEvent): void {
        if (!this.timerInterval) {
            // console.warn("Keypress ignored - no active round");
            return;
        }

        // console.log("Pressed:", event.key, "Expected:", this.currentKey);

        const wasCorrect: boolean = event.key.toUpperCase() === this.currentKey.toUpperCase();
        // console.log("Match result:", wasCorrect);

        // Visual feedback
        this.promptElement.classList.add(wasCorrect ? "correct" : "incorrect");
        setTimeout(() => {
            this.promptElement.classList.remove("correct", "incorrect");
        }, 200);

        if (wasCorrect) {
            this.successfulDodge();
        }
        else {
            this.failedDodge();
        }
    }

    /**
     * Called when player successfully presses the correct key.
     * @private
     */
    private successfulDodge(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = undefined;
        }
        // console.log("before showcutscene dodge request");

        // Show dodge cutscene
        void this.showCutscene("dodge").then(() => {
            // console.log("after showcutscene dodge request");
            // Attack the monster
            this.monsterHealth -= 25;
            this.monsterHealthBar.style.width = `${this.monsterHealth}%`;

            if (this.monsterHealth <= 0) {
                this.battleWon();
            }
            else {
                // Start next round
                this.startNewRound();
            }
        });
    }

    /**
     * Called when player fails to press the correct key.
     * @private
     */
    private failedDodge(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = undefined;
        }

        // Show failure cutscene
        void this.showCutscene("fail").then(() => {
            // End game - player died
            void this.handleBattleLost();
        }).catch((error: unknown) => {
            console.error("Error showing fail cutscene:", error);
            void this.handleBattleLost(); // Still proceed even if cutscene fails
        });
    }

    /**
     * Shows a cutscene for the battle.
     * @param type - The type of cutscene to show ("dodge" or "fail")
     * @private
     */
    private async showCutscene(type: string): Promise<void> {
        // console.log(type);
        switch (type) {
            case "fail":
                this.cutsceneNumber = 4;
                break;
            case "dodge":
                this.cutsceneNumber = 5;
                break;
            case "won":
                this.cutsceneNumber = 6;
                break;
        }
        if (!this.cutsceneNumber) return Promise.resolve();

        return new Promise(resolve => {
            const cutscene: HTMLElement = document.createElement("cut-scene");
            cutscene.setAttribute("cutscene-number", (this.cutsceneNumber).toString());
            this.shadowRoot!.appendChild(cutscene);

            const observer: MutationObserver = new MutationObserver(() => {
                if (!this.shadowRoot?.contains(cutscene)) {
                    observer.disconnect();
                    resolve(); // Cutscene is gone!
                }
            });
            observer.observe(this.shadowRoot!, { childList: true });
        });
    }

    private battleWon(): void {
        void this.showCutscene("won").then(() => {
            // End game - player died
            void this.handleBattleWon();
        }).catch((error: unknown) => {
            console.error("Error showing fail cutscene:", error);
            void this.handleBattleWon(); // Still proceed even if cutscene fails
        });
    }

    /**
     * Handles the player winning the battle.
     * @private
     */
    private async handleBattleWon(): Promise<void> {
        await this._gameRouteService.seaBeastDefeated();
        const canvasComponent: CanvasComponent | null = document.querySelector("game-root");

        if (canvasComponent && canvasComponent.shadowRoot) {
            const gameCanvas: HTMLElement | null = canvasComponent.shadowRoot.querySelector("game-canvas");
            if (gameCanvas) {
                void (gameCanvas as CanvasComponent).refreshGameState();
            }
        }
    }

    /**
     * Handles the player losing the battle.
     * @private
     */
    private async handleBattleLost(): Promise<void> {
        await this._gameRouteService.killedBySeaBeast();
        const canvasComponent: CanvasComponent | null = document.querySelector("game-root");

        if (canvasComponent && canvasComponent.shadowRoot) {
            const gameCanvas: HTMLElement | null = canvasComponent.shadowRoot.querySelector("game-canvas");
            if (gameCanvas) {
                void (gameCanvas as CanvasComponent).refreshGameState();
            }
        }
    }

    public disconnectedCallback(): void {
        document.removeEventListener("keydown", this.boundHandleKeyPress);
        if (this.countdownInterval) clearInterval(this.countdownInterval);
        if (this.timerInterval) clearInterval(this.timerInterval);
    }
}
